/* eslint-disable max-lines */
import React, { useEffect, useRef, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types'; // Importing PropTypes for type checking
import { FACETS_FIELD_KEY } from '@tcp/core/src/services/abstractors/productListing/productListing.utils';
import { getViewportInfo } from '@tcp/core/src/utils';
import { getLabelFromCopyOrFromTranslation } from '@tcp/core/src/utils/labels/labelUtilty';
import filterChipsLabels from '@tcp/core/src/components/common/atoms/FilterChips/views/FilterChips.labels.js';
import {
  getFiltersPayload,
  getFiltersPayloadSeo,
  getPlpUrlQueryValuesSeo,
  getPlpUrlQueryValues,
} from '@tcp/core/src/components/features/browse/CategoryListingPage/container/CategoryListingPage.utils.js';
import {
  StyledCategoryModuleFiltersWrapper,
  StyledButton,
  CategoryContainer,
  CategoryScrollContainer,
  ProductScrollContainer,
  ProductList,
  SelectedChip,
  RemoveChip,
  ClearAll,
  StyledFilterContainer,
} from '../styles/CategoryModuleFilter.style';
import * as pluralize from '../../../../../../../utils/pluralize';

// Utility function to extract cat1 array
export const extractCat = (categoryModuleFilters, index) => {
  if (categoryModuleFilters && Object.keys(categoryModuleFilters)?.length > 0) {
    return categoryModuleFilters[Object.keys(categoryModuleFilters)[index]]?.map(
      (item) => item.displayName
    );
  }
  return [];
};
const priceRangeDisplayFunc = (displayName) => {
  if (!displayName) return null;

  const parts = displayName.split(' ');
  // eslint-disable-next-line no-restricted-globals
  if (parts.length !== 3 || isNaN(parts[0]) || isNaN(parts[2])) {
    return <span className="price-label">{displayName}</span>;
  }

  const [min, , max] = parts;
  return <span className="price-label">{`$${min} to $${max}`}</span>;
};

const checkForColorFilter = (key) =>
  key === 'TCPColor_uFilter' || key?.toLowerCase() === 'TCPColor_M_uFilter'?.toLowerCase();

const getDisplayName = (item) => {
  const priceRangeFilter = 'min_offer_price';
  if (item?.facetName === priceRangeFilter) {
    return priceRangeDisplayFunc(item?.displayName);
  }
  if (checkForColorFilter(item?.facetName)) {
    return pluralize(
      item?.displayName?.charAt(0).toUpperCase() + item?.displayName?.slice(1).toLowerCase()
    );
  }
  if (item?.facetName === 'TCPBazaarVoiceRating_inText_uFilter') {
    return item?.displayName?.replace('and', '&');
  }
  if (item?.facetName === 'TCPStyleQTYUS_uFilter') {
    return `${item?.displayName}-Pack`;
  }
  return item?.displayName;
};

const getFilterItemDisplayNameFromValue = (filtersMap, value, facetName) => {
  // if facet name is price change directly call price dispaly
  const facetItems = filtersMap[facetName];
  if (!Array.isArray(facetItems)) {
    return facetName === 'min_offer_price' ? priceRangeDisplayFunc(value) : value;
  }

  const matchedItem = facetItems.find((item) => item.id === value);
  return matchedItem ? getDisplayName(matchedItem) : value;
};

export const getAppliedFiltersCount = (appliedFilters) => {
  let count = 0;
  // returns count for the applied filters.
  // eslint-disable-next-line
  for (let key in appliedFilters) {
    count +=
      key.toLowerCase() !== FACETS_FIELD_KEY.sort && key !== 'bopisStoreId'
        ? appliedFilters[key].length
        : 0;
  }
  return count;
};

const getIdsArray = (filterMap) => {
  return filterMap?.map((item) => item?.id) || [];
};
const getCategoryKeys = (moduleFilters) => {
  return moduleFilters ? Object.keys(moduleFilters) : [];
};

export const getUpdatedUrl = ({
  filtersFromUrl,
  asPath,
  isFacetedNavigationEnabled,
  seoFacetedNavigationConfig,
  cleanUrlObj,
}) => {
  if (isFacetedNavigationEnabled) {
    return getPlpUrlQueryValuesSeo(
      filtersFromUrl,
      asPath,
      true,
      1,
      seoFacetedNavigationConfig,
      cleanUrlObj
    );
  }
  return getPlpUrlQueryValues(filtersFromUrl, asPath, true, 1);
};

export const getParams = ({
  key,
  newList,
  formData,
  isFacetedNavigationEnabled,
  pathName,
  seoFacetedNavigationConfig,
  cleanUrlObj,
}) => {
  const updatedFormDetails = {
    ...formData,
    [key]: newList,
  };
  if (isFacetedNavigationEnabled) {
    return getFiltersPayloadSeo(
      pathName,
      updatedFormDetails,
      isFacetedNavigationEnabled,
      seoFacetedNavigationConfig,
      cleanUrlObj
    );
  }
  return getFiltersPayload(pathName, updatedFormDetails);
};

const handleRemoveFilter = ({
  fieldName,
  removedValue,
  list,
  updateBopisFormValue,
  isFacetedNavigationEnabled,
  pathName,
  seoFacetedNavigationConfig,
  cleanUrlObj,
  formDetails,
  setPriceFilterFlagActn,
  setUpdatedUrlActn,
}) => {
  const tmpArr = list?.filter((item) => item !== removedValue);
  updateBopisFormValue(fieldName, tmpArr);
  const { asPath, filtersFromUrl } =
    getParams({
      key: fieldName,
      newList: tmpArr,
      formData: formDetails,
      isFacetedNavigationEnabled,
      pathName,
      seoFacetedNavigationConfig,
      cleanUrlObj,
    }) || {};
  const updatedUrl = getUpdatedUrl({
    filtersFromUrl,
    asPath,
    isFacetedNavigationEnabled,
    seoFacetedNavigationConfig,
    cleanUrlObj,
  });
  setPriceFilterFlagActn(true);
  setUpdatedUrlActn({ updatedUrl });
};

const renderRemoveChip = ({
  facet,
  item,
  isLoadingMore,
  updateBopisFormValue,
  isFacetedNavigationEnabled,
  pathName,
  seoFacetedNavigationConfig,
  cleanUrlObj,
  formDetails,
  setPriceFilterFlagActn,
  setUpdatedUrlActn,
  appliedFilters,
}) => (
  <RemoveChip
    onClick={(e) => {
      if (!isLoadingMore) {
        e.preventDefault();
        handleRemoveFilter({
          fieldName: facet,
          removedValue: item,
          list: appliedFilters[facet],
          updateBopisFormValue,
          isFacetedNavigationEnabled,
          pathName,
          seoFacetedNavigationConfig,
          cleanUrlObj,
          formDetails,
          setPriceFilterFlagActn,
          setUpdatedUrlActn,
        });
      }
    }}
  >
    &#x2715;
  </RemoveChip>
);
const renderSelectedChip = ({
  facet,
  item,
  filterMaps,
  isLoadingMore,
  updateBopisFormValue,
  isFacetedNavigationEnabled,
  pathName,
  seoFacetedNavigationConfig,
  cleanUrlObj,
  formDetails,
  setPriceFilterFlagActn,
  setUpdatedUrlActn,
  appliedFilters,
}) => (
  <SelectedChip key={item.id || item}>
    {getFilterItemDisplayNameFromValue(filterMaps, item, facet)}
    {renderRemoveChip({
      facet,
      item,
      isLoadingMore,
      updateBopisFormValue,
      isFacetedNavigationEnabled,
      pathName,
      seoFacetedNavigationConfig,
      cleanUrlObj,
      formDetails,
      setPriceFilterFlagActn,
      setUpdatedUrlActn,
      appliedFilters,
    })}
  </SelectedChip>
);
export const renderAppliedFilters = ({
  appliedFilters,
  filterMaps,
  isLoadingMore,
  updateBopisFormValue,
  isFacetedNavigationEnabled,
  pathName,
  seoFacetedNavigationConfig,
  cleanUrlObj,
  formDetails,
  setPriceFilterFlagActn,
  setUpdatedUrlActn,
}) => {
  return Object.keys(appliedFilters)?.map((facet) => {
    return (
      facet !== 'bopisStoreId' &&
      !isEmpty(appliedFilters) &&
      appliedFilters[facet]?.map((item) =>
        renderSelectedChip({
          facet,
          item,
          filterMaps,
          isLoadingMore,
          updateBopisFormValue,
          isFacetedNavigationEnabled,
          pathName,
          seoFacetedNavigationConfig,
          cleanUrlObj,
          formDetails,
          setPriceFilterFlagActn,
          setUpdatedUrlActn,
          appliedFilters,
        })
      )
    );
  });
};

const lblClear = getLabelFromCopyOrFromTranslation(
  'PLP.PLP_sort_filter.lbl_clear',
  filterChipsLabels
);

const renderFilterSection = ({
  appliedFilters,
  cat1,
  cat2,
  filterMaps,
  removeAllFilters,
  isLoadingMore,
  updateBopisFormValue,
  isFacetedNavigationEnabled,
  pathName,
  seoFacetedNavigationConfig,
  cleanUrlObj,
  formDetails,
  setPriceFilterFlagActn,
  setUpdatedUrlActn,
  isHideAppliedFilters,
}) => {
  const isDesktop = getViewportInfo()?.isDesktop;
  if (isHideAppliedFilters) return null;
  return (
    getAppliedFiltersCount(appliedFilters) > 0 &&
    isDesktop &&
    (cat1?.length > 0 || cat2?.length > 0) && (
      <StyledFilterContainer>
        {renderAppliedFilters({
          appliedFilters,
          filterMaps,
          isLoadingMore,
          updateBopisFormValue,
          isFacetedNavigationEnabled,
          pathName,
          seoFacetedNavigationConfig,
          cleanUrlObj,
          formDetails,
          setPriceFilterFlagActn,
          setUpdatedUrlActn,
        })}
        <ClearAll onClick={removeAllFilters}>{lblClear}</ClearAll>
      </StyledFilterContainer>
    )
  );
};

const overFlowingCheckDesktop = (overflowing) => {
  const isMobile = getViewportInfo()?.isMobile;
  return overflowing && !isMobile;
};

const renderScrollButton = (
  isOverflowing,
  scrollFunc,
  direction,
  variant,
  listLength,
  scrollPosition,
  scrollRef
) => {
  if (!overFlowingCheckDesktop(isOverflowing) || listLength == null || listLength === 0) {
    return null; // Return null if there is no overflow
  }

  // Check if we should hide the button
  if (scrollRef?.current) {
    const { scrollWidth, clientWidth } = scrollRef.current;
    if (direction === 'left' && scrollPosition <= 2) {
      return null;
    }
    if (direction === 'right' && scrollPosition >= scrollWidth - clientWidth) {
      return null;
    }
  }

  return direction === 'right' ? (
    <StyledButton
      onClick={scrollFunc}
      variant={variant}
      className="module-filter-right-scroll-button"
    >
      &#x276F;
    </StyledButton>
  ) : (
    <StyledButton
      onClick={scrollFunc}
      variant={variant}
      className="module-filter-left-scroll-button"
    >
      &#x276E;
    </StyledButton>
  );
};

const createScrollHandler =
  (ref, direction = 'left', amount = 150) =>
  () => {
    const currentRef = ref.current; // Create a new reference to ref.current
    if (currentRef) {
      const newScrollPosition = currentRef.scrollLeft + (direction === 'left' ? -amount : amount);
      currentRef.scrollLeft = newScrollPosition;
    }
  };

const checkProductListLong = (overflowing, cat) => !overflowing || cat?.length <= 4;

const CategoryModuleFilter = ({
  categoryModuleFilters,
  updateBopisFormValue,
  pathName,
  formDetails,
  isFacetedNavigationEnabled,
  seoFacetedNavigationConfig,
  cleanUrlObj,
  setUpdatedUrlActn,
  appliedFilters,
  isLoadingMore,
  setPriceFilterFlagActn,
  filterMaps,
  isHideAppliedFilters,
  isPLPSortAndOneHrFilterRedesignEnabled,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const cat1 = extractCat(categoryModuleFilters, 0);
  const cat2 = extractCat(categoryModuleFilters, 1);
  // Retrieve the category keys
  const catKeys = getCategoryKeys(categoryModuleFilters);
  const [cat1Key, cat2Key] = catKeys;

  const cat1idsArray = getIdsArray(filterMaps[cat1Key]);
  const cat2idsArray = getIdsArray(filterMaps[cat2Key]);

  const [selectedCategories, setSelectedCategories] = useState([]);

  const [selectedProductTypes, setSelectedProductTypes] = useState([]);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isProductOverflowing, setIsProductOverflowing] = useState(false);
  const [categoryScrollPosition, setCategoryScrollPosition] = useState(0);
  const [productScrollPosition, setProductScrollPosition] = useState(0);
  const isMobile = getViewportInfo()?.isMobile;

  const categoryScrollRef = useRef(null);
  const productScrollRef = useRef(null);

  useEffect(() => {
    const handleCategoryScroll = () => {
      if (categoryScrollRef.current) {
        setCategoryScrollPosition(categoryScrollRef.current.scrollLeft);
      }
    };

    const categoryRef = categoryScrollRef.current;
    if (categoryRef) {
      categoryRef.addEventListener('scroll', handleCategoryScroll);
      handleCategoryScroll();
    }

    return () => {
      if (categoryRef) {
        categoryRef.removeEventListener('scroll', handleCategoryScroll);
      }
    };
  }, [categoryScrollRef?.current]);

  useEffect(() => {
    const handleProductScroll = () => {
      if (productScrollRef.current) {
        setProductScrollPosition(productScrollRef.current.scrollLeft);
      }
    };

    const productRef = productScrollRef.current;
    if (productRef) {
      productRef.addEventListener('scroll', handleProductScroll);
      handleProductScroll();
    }

    return () => {
      if (productRef) {
        productRef.removeEventListener('scroll', handleProductScroll);
      }
    };
  }, [productScrollRef.current]);
  useEffect(() => {
    const checkOverflow = () => {
      if (categoryScrollRef.current) {
        const { scrollWidth, clientWidth } = categoryScrollRef.current;
        setIsOverflowing(scrollWidth > clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [cat1]);

  useEffect(() => {
    const checkProductOverflow = () => {
      if (productScrollRef.current) {
        const { scrollWidth, clientWidth } = productScrollRef.current;
        setIsProductOverflowing(scrollWidth > clientWidth);
      }
    };

    checkProductOverflow();
    window.addEventListener('resize', checkProductOverflow);
    return () => window.removeEventListener('resize', checkProductOverflow);
  }, [cat2]);

  useEffect(() => {
    setSelectedCategories(formDetails[cat1Key] || []);
    setSelectedProductTypes(formDetails[cat2Key] || []);
  }, [formDetails[cat1Key], formDetails[cat2Key]]);

  const removeAllFilters = () => {
    Object.keys(appliedFilters).forEach((filter) => {
      if (filter !== 'sort') {
        updateBopisFormValue(filter, []);
      }
    });
    const { asPath, filtersFromUrl } =
      getParams({
        formData: {},
        isFacetedNavigationEnabled,
        pathName,
        seoFacetedNavigationConfig,
        cleanUrlObj,
      }) || {};
    const updatedUrl = getUpdatedUrl({
      filtersFromUrl,
      asPath,
      isFacetedNavigationEnabled,
      seoFacetedNavigationConfig,
      cleanUrlObj,
    });
    setPriceFilterFlagActn(true);
    setUpdatedUrlActn({ updatedUrl });
  };

  const toggleSelection = (value, setList, key) => {
    setList((prev) => {
      const newList = prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
      updateBopisFormValue(key, newList);

      const { asPath, filtersFromUrl } =
        getParams({
          key,
          newList,
          formData: formDetails,
          isFacetedNavigationEnabled,
          pathName,
          seoFacetedNavigationConfig,
          cleanUrlObj,
        }) || {};
      const updatedUrl = getUpdatedUrl({
        filtersFromUrl,
        asPath,
        isFacetedNavigationEnabled,
        seoFacetedNavigationConfig,
        cleanUrlObj,
      });

      setUpdatedUrlActn({ updatedUrl });
      return newList;
    });
  };

  const handleSoftDisabledClick = (value, setList, key) => {
    removeAllFilters();
    const newList = [value];
    updateBopisFormValue(key, newList);
    const { asPath, filtersFromUrl } =
      getParams({
        key,
        newList,
        formData: {},
        isFacetedNavigationEnabled,
        pathName,
        seoFacetedNavigationConfig,
        cleanUrlObj,
      }) || {};
    const updatedUrl = getUpdatedUrl({
      filtersFromUrl,
      asPath,
      isFacetedNavigationEnabled,
      seoFacetedNavigationConfig,
      cleanUrlObj,
    });

    setUpdatedUrlActn({ updatedUrl });
    setList(newList);
  };

  const scrollLeft = createScrollHandler(categoryScrollRef, 'left');
  const scrollRight = createScrollHandler(categoryScrollRef, 'right');
  const productScrollLeft = createScrollHandler(productScrollRef, 'left');
  const productScrollRight = createScrollHandler(productScrollRef, 'right');
  return (
    <>
      {cat1?.length > 0 || cat2?.length > 0 ? (
        <StyledCategoryModuleFiltersWrapper
          className="category-product-module-filter-container"
          isPLPSortAndOneHrFilterRedesignEnabled={isPLPSortAndOneHrFilterRedesignEnabled}
        >
          {cat1?.length > 0 && (
            <CategoryScrollContainer
              className="module-filter-category-scroll-container"
              isPLPSortAndOneHrFilterRedesignEnabled={isPLPSortAndOneHrFilterRedesignEnabled}
            >
              {renderScrollButton(
                isOverflowing,
                scrollLeft,
                'left',
                'scroll',
                cat1?.length,
                categoryScrollPosition,
                categoryScrollRef
              )}
              {cat1?.length > 0 && (
                <CategoryContainer
                  ref={categoryScrollRef}
                  isMobile={isMobile}
                  isOverflowing={isOverflowing}
                  isPLPSortAndOneHrFilterRedesignEnabled={isPLPSortAndOneHrFilterRedesignEnabled}
                  className="module-filter-category-container"
                >
                  {cat1?.map((cat) => (
                    <StyledButton
                      key={cat}
                      selected={selectedCategories.includes(cat)}
                      onClick={() => {
                        if (cat1idsArray?.includes(cat)) {
                          toggleSelection(cat, setSelectedCategories, cat1Key);
                        } else {
                          handleSoftDisabledClick(cat, setSelectedCategories, cat1Key);
                        }
                      }}
                      softDisable={!cat1idsArray?.includes(cat)}
                      isPLPSortAndOneHrFilterRedesignEnabled={
                        isPLPSortAndOneHrFilterRedesignEnabled
                      }
                      className={`module-filter-category-pills ${
                        selectedCategories.includes(cat) ? 'selected-pill' : ''
                      } ${!cat1idsArray?.includes(cat) ? 'soft-disabled-pill' : ''}`}
                    >
                      {checkForColorFilter(cat1Key)
                        ? pluralize(cat?.charAt(0).toUpperCase() + cat?.slice(1).toLowerCase())
                        : cat?.charAt(0).toUpperCase() + cat?.slice(1).toLowerCase()}
                    </StyledButton>
                  ))}
                </CategoryContainer>
              )}

              {renderScrollButton(
                isOverflowing,
                scrollRight,
                'right',
                'scroll',
                cat1?.length,
                categoryScrollPosition,
                categoryScrollRef
              )}
            </CategoryScrollContainer>
          )}

          {cat2?.length > 0 && (
            <ProductScrollContainer
              className="module-filter-product-scroll-container"
              isPLPSortAndOneHrFilterRedesignEnabled={isPLPSortAndOneHrFilterRedesignEnabled}
            >
              {renderScrollButton(
                isProductOverflowing,
                productScrollLeft,
                'left',
                'productScroll',
                cat2?.length,
                productScrollPosition,
                productScrollRef
              )}

              <ProductList
                ref={productScrollRef}
                fitScreen={checkProductListLong(isProductOverflowing, cat2)}
                isProductOverflowing={isProductOverflowing}
                className="module-filter-product-list"
                isPLPSortAndOneHrFilterRedesignEnabled={isPLPSortAndOneHrFilterRedesignEnabled}
              >
                {cat2?.map((type) => (
                  <StyledButton
                    key={type}
                    selected={selectedProductTypes.includes(type)}
                    onClick={() => {
                      if (cat2idsArray?.includes(type)) {
                        toggleSelection(type, setSelectedProductTypes, cat2Key);
                      } else {
                        handleSoftDisabledClick(type, setSelectedProductTypes, cat2Key);
                      }
                    }}
                    softDisable={!cat2idsArray?.includes(type)}
                    secondCategory={true}
                    className={`module-filter-product-pills ${
                      selectedProductTypes.includes(type) ? 'selected-pill' : ''
                    } ${!cat2idsArray?.includes(type) ? 'soft-disabled-pill' : ''}`}
                    isPLPSortAndOneHrFilterRedesignEnabled={isPLPSortAndOneHrFilterRedesignEnabled}
                  >
                    {type}
                  </StyledButton>
                ))}
              </ProductList>

              {renderScrollButton(
                isProductOverflowing,
                productScrollRight,
                'right',
                'productScroll',
                cat2?.length,
                productScrollPosition,
                productScrollRef
              )}
            </ProductScrollContainer>
          )}
          {renderFilterSection({
            appliedFilters,
            cat1,
            cat2,
            filterMaps,
            removeAllFilters,
            isLoadingMore,
            updateBopisFormValue,
            isFacetedNavigationEnabled,
            pathName,
            seoFacetedNavigationConfig,
            cleanUrlObj,
            formDetails,
            setPriceFilterFlagActn,
            setUpdatedUrlActn,
            isHideAppliedFilters,
          })}
        </StyledCategoryModuleFiltersWrapper>
      ) : null}
    </>
  );
};

// Define propTypes for the component
CategoryModuleFilter.propTypes = {
  categoryModuleFilters: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        displayName: PropTypes.string.isRequired,
      })
    )
  ),
  updateBopisFormValue: PropTypes.func,
  pathName: PropTypes.string,
  formDetails: PropTypes.objectOf(
    PropTypes.shape({
      // Define the expected structure of formDetails here
    })
  ),
  isFacetedNavigationEnabled: PropTypes.bool,
  seoFacetedNavigationConfig: PropTypes.objectOf(
    PropTypes.shape({
      // Define the expected structure of seoFacetedNavigationConfig here
    })
  ),
  cleanUrlObj: PropTypes.objectOf(
    PropTypes.shape({
      // Define the expected structure of cleanUrlObj here
    })
  ),
  setUpdatedUrlActn: PropTypes.func,
  appliedFilters: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  isLoadingMore: PropTypes.bool,
  setPriceFilterFlagActn: PropTypes.func,
  filterMaps: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
      })
    )
  ),
};

// Define default props
CategoryModuleFilter.defaultProps = {
  categoryModuleFilters: {},
  updateBopisFormValue: () => {},
  pathName: '',
  formDetails: {},
  isFacetedNavigationEnabled: false,
  seoFacetedNavigationConfig: {},
  cleanUrlObj: {},
  setUpdatedUrlActn: () => {},
  appliedFilters: {},
  isLoadingMore: false,
  setPriceFilterFlagActn: () => {},
  filterMaps: {},
};

export default CategoryModuleFilter;
